
import React, { useLayoutEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { toDollar } from 'utils'

import { Box, Grid, Stack, Divider, Typography } from '@mui/material'

import { Card } from '@repo/ui'
import { CategoryCell, MoreCategoriesTooltip, ProductModelHover, TagCapabilityBadges } from 'components'
import { isTagFormFactor, primaryTagModel, readRange, type TagCapability } from 'constants/tagCatalog'
import { isHardwareProduct } from 'constants/tagShopProductTypes'
import type { TagShopProduct } from 'hooks/api/useTagProducts'
import { ROUTES } from 'routes'

// The "Best for" chip zone is capped at this many rows. A fixed zone keeps every
// card in the grid the same height regardless of how many categories a product
// has; the "+N more" pill absorbs the overflow.
//
// The cap is expressed in ROWS, not pixels, and the trim pass below counts actual
// rendered rows. An earlier version hard-coded 84px as "~2 rows at a 42px pitch";
// when the label spacing changed the real pitch became 28px, so 84px silently
// started admitting a THIRD row. A pixel budget encodes a row count only as long
// as nobody touches the type or spacing, which is not a safe assumption.
const BEST_FOR_ROWS = 2
// Measured row pitch (14px chip text + gap). Only used to reserve a stable CSS
// height so cards line up; the row cap itself does not depend on it being exact.
const CHIP_ROW_PITCH = 28
const CHIP_ZONE_HEIGHT = BEST_FOR_ROWS * CHIP_ROW_PITCH

// Every clamped text block on this card is expressed as a LINE COUNT, and its box
// height is derived from that count -- never set independently.
//
// The bug this closes: the hardware "Description" carried `WebkitLineClamp: 5` AND
// `height: CHIP_ZONE_HEIGHT` (56px), two numbers that disagreed. 5 lines at
// lineHeight 1.5 on body3's 14px is 105px, so the 56px box won, `overflow: hidden`
// cut the text at 2.67 lines -- mid-line, and with NO ellipsis, because the clamp
// (which is what draws the "...") was never the binding constraint. CHIP_ZONE_HEIGHT
// was sized for two rows of CHIPS; it was never a text budget.
const DESC_LINE_HEIGHT = 1.5
// Brief blurb under the title. Fixed at 2 lines so a one-line description still
// reserves two lines' worth of space and the capability badges stay aligned across
// every card in the grid.
const BRIEF_DESC_LINES = 2
// Hardware cards replace the whole Capabilities / Sizes / Range stack with this one
// block, so it gets the room those sections would have used.
const HARDWARE_DESC_LINES = 5
const clampLinesSx = (lines: number) => ({
  lineHeight: DESC_LINE_HEIGHT,
  // `em` resolves against the element's own font-size and the line-height is
  // unitless, so N lines is exactly N * DESC_LINE_HEIGHT em. Keeping the height and
  // the clamp derived from one number is what makes the ellipsis render: the clamp
  // has to be what truncates, not the box.
  height: `${lines * DESC_LINE_HEIGHT}em`,
  overflow: 'hidden',
  display: '-webkit-box',
  WebkitLineClamp: lines,
  WebkitBoxOrient: 'vertical',
})

// A form spans several sizes (variant option). Show the span as a compact range —
// "30–45mm" for numeric mm, "S–L" for letter sizes — so the card conveys sizing
// without listing every variant.
const LETTER_ORDER = ['XS', 'S', 'M', 'L', 'XL', 'XXL']
function formatSizeRange(sizes: string[]): string {
  const uniq = [...new Set(sizes.filter(Boolean))]
  if (!uniq.length) return ''
  if (uniq.every(s => /^\d+(\.\d+)?\s*mm$/i.test(s))) {
    const nums = uniq.map(s => parseFloat(s)).sort((a, b) => a - b)
    return nums.length > 1 ? `${nums[0]}–${nums[nums.length - 1]}mm` : `${nums[0]}mm`
  }
  const letters = uniq
    .filter(s => LETTER_ORDER.includes(s.toUpperCase()))
    .sort((a, b) => LETTER_ORDER.indexOf(a.toUpperCase()) - LETTER_ORDER.indexOf(b.toUpperCase()))
  if (letters.length) return letters.length > 1 ? `${letters[0]}–${letters[letters.length - 1]}` : letters[0]
  return uniq.join(' · ')
}

// Parse the numeric tap-distance bounds out of a readRange() string
// ("~1 cm (touch)", "~2–3 cm (tap)", "~4 cm (tap)"). Base capability (Verify)
// never carries the UHF suffix, so a plain number scan is enough.
function tapBounds(range: string): { lo: number; hi: number } | null {
  const nums = range.match(/\d+(?:\.\d+)?/g)
  if (!nums) return null
  return { lo: parseFloat(nums[0]), hi: parseFloat(nums[nums.length - 1]) }
}

interface TagShopProductPreviewProps {
  /**
   * ⚠️ WAS `object`, which types every property access on this card as an error.
   *
   * VRG-780 — the card already carried EIGHT `TS2339: Property 'variants' does not
   * exist on type 'object'`-class errors, one per field it reads, and adding a read
   * of `form_factor` added two more. `object` is not a loose type here, it is the
   * absence of one: it admits any shape and then forbids reading anything off it, so
   * the file's real contract lived in the eight errors rather than in the signature.
   *
   * `TagShopProduct` is the type the only caller already passes
   * (`Shop.tsx` spreads a `useTagProducts` row), so this narrows nothing in practice
   * and removes ten errors from the platform's count.
   */
  product: TagShopProduct,
}

export const TagsShopProductPreview = (props: TagShopProductPreviewProps) => {
  const { product } = props
  const navigate = useNavigate()
  // "From" price = the cheapest variant's unit price (a form spans capabilities,
  // so the starting price is the lowest, not the priciest variant).
  const variants = product.variants ?? []
  const minUnit = variants.length ? Math.min(...variants.map((v: any) => v.unit_price)) : 0

  const bestFor: string[] = product.best_for_product_types ?? []
  // Capabilities this form offers (Connect/Verify/… ) → unioned leaf badges.
  const capabilities: TagCapability[] = (product.capabilities ?? []) as TagCapability[]
  // Brief description (title alone isn't descriptive enough) — strip HTML, clamp on render.
  const plainDescription = String(product.description ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
  // Size span across variants → compact range shown under the badges.
  const sizeRange = formatSizeRange(variants.map((v: any) => v.size).filter(Boolean))

  // Read-range span across the product's sizes (VRG-156). NFC tap range is antenna/
  // size-driven, so computing readRange at the base (Verify) capability for every size
  // and taking the min low / max high bounds the tap distance. A ladder offering
  // Verify + Trace (EM4425 dual-freq) additionally reads far-field with a UHF reader.
  // Value only — the "Range" field label carries the noun now, so we drop the
  // trailing "scan range" wording (kept the far-field RFID note for Trace ladders).
  const rangeValue = (() => {
    const sizes = variants.map((v: any) => v.size).filter(Boolean) as string[]
    const bounds = sizes.map(sz => tapBounds(readRange('Verify', sz))).filter(Boolean) as { lo: number; hi: number }[]
    if (!bounds.length) return ''
    const lo = Math.min(...bounds.map(b => b.lo))
    const hi = Math.max(...bounds.map(b => b.hi))
    const cm = lo === hi ? `~${lo} cm` : `~${lo}–${hi} cm`
    const hasTrace = capabilities.includes('Verify + Trace' as TagCapability)
    return `${cm}${hasTrace ? ' · up to ~10 m (RFID)' : ''}`
  })()

  // Measured overflow: chip widths vary (some categories are 1 word, some are
  // "Wine & spirits"), so a fixed chip count can't express "fill the zone then
  // show +N more" — wide chips wrap 1-per-row and clip the pill. Instead we
  // render as many chips as actually fit in CHIP_ZONE_HEIGHT and always keep the
  // "+N more" pill as the last visible item.
  const zoneRef = useRef<HTMLDivElement | null>(null)
  const [visibleCount, setVisibleCount] = useState<number>(bestFor.length)

  // Re-expand to the full set whenever the product (its categories) changes, so
  // the measure pass below recomputes from scratch rather than inheriting a
  // smaller count from a previous product.
  const bestForKey = bestFor.join('|')
  useLayoutEffect(() => {
    setVisibleCount(bestFor.length)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bestForKey])

  // Trim one item at a time until everything visible (chips plus the "+N more"
  // pill) occupies at most BEST_FOR_ROWS rows.
  //
  // Rows are counted by distinct rendered top offsets rather than compared against
  // a pixel budget: chips wrap, so their count says nothing about row count, and a
  // pixel budget goes stale the moment the type or spacing changes.
  useLayoutEffect(() => {
    const zone = zoneRef.current
    if (!zone) return
    const items = [...zone.children] as HTMLElement[]
    if (!items.length) return
    const rows = new Set(items.map(el => Math.round(el.getBoundingClientRect().top)))
    if (rows.size > BEST_FOR_ROWS && visibleCount > 1) {
      setVisibleCount(v => v - 1)
    }
  }, [visibleCount, bestForKey])

  const shown = bestFor.slice(0, visibleCount)
  const overflow = bestFor.length - shown.length

  // Hardware products (e.g. the Desk Reader) aren't "best for" a set of product
  // categories the way tags are, so show their description instead of chips.
  const isHardware = isHardwareProduct(product)

  /* VRG-780 — the GLB for this tile, or `undefined`.
     `form_factor` arrives from Shopify as a loose string, so it is NARROWED rather
     than cast: a cast would compile and then resolve `VERA-undefined-…`. Hardware
     is excluded because the model library is tags; a Desk Reader has no plate and
     `primaryTagModel` would return undefined anyway, so this is a statement of
     intent rather than a guard that does work. */
  const modelSrc = !isHardware && isTagFormFactor(product.form_factor)
    ? primaryTagModel(product.form_factor)
    : undefined

  return (
    <Card sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ pt: 6, pb: 4, px: 6, display: 'flex', justifyContent: 'center' }}>
        {/* Centered square product image — a touch smaller than the full card width
            with breathing room around it. Source images are square, so a square box
            fits them cleanly. Larger footprint gives the product more presence.

            VRG-780 — and it SPINS on hover, when the form has a model. The still is
            unchanged and is what the tile shows at rest; `ProductModelHover` adds the
            second state and nothing else, so a form with no model renders exactly the
            box that was here before, with no affordance, no listeners and no tab stop.

            ⚠️ The model is resolved from the FORM, through the same
            `PHOTOGRAPHED_SIZE` map that chose the still, which is what stops the two
            disagreeing — see `primaryTagModel`. The grid is product-level, so the form
            is the only appearance it can name; the per-appearance resolver
            (`tagModelAsset`) is for the product page, where a buyer picks a size and a
            colourway and the model has to follow them. */}
        <Box sx={{ width: '100%', maxWidth: 260 }}>
          <ProductModelHover
            src={product.images[0]}
            modelSrc={modelSrc}
            title={product.title}
            aspectRatio="1 / 1"
          />
        </Box>
      </Box>
      <Divider />
      {/* pt trimmed from 6 to 4 (24px -> 16px): the image block above already carries
          pb:4, so the two stacked into an oversized gap between the artwork and the
          title. Sides and bottom keep the 24px gutter. */}
      <Stack spacing={4} sx={{ pt: 4, pb: 6, px: 6, flex: 1 }}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body1" noWrap>
            {product.title}
          </Typography>
          <Stack alignItems="right" justifyContent="right">
            <Typography variant='body3' sx={{ textAlign: 'right' }} noWrap>
              {isHardware ? 'Unit price' : 'From'}
            </Typography>
            <Typography variant='body3' color="text.secondary" sx={{ textAlign: 'right' }} noWrap>
              {/* Hardware is priced per unit; tags show a "from <cheapest>" starting price. */}
              {toDollar(minUnit)}{isHardware ? '' : ' / tag'}
            </Typography>
          </Stack>
        </Stack>
        {/* Brief description — the title alone isn't descriptive enough. Reserves a
            FIXED 2-line height (min + max) so a 1-line description still occupies two
            lines' worth of space — keeps the capability badges aligned across every
            card in the grid regardless of description length. */}
        {/*
          `pb: 2` gives the description its own break from the spec fields below.
          Measured, the card ran a UNIFORM 16px between all five blocks — header,
          description, Capabilities, Sizes/Range, Best for — so prose about the product was
          spaced identically to the structured specs and read as just one more section in a
          flat list of five. That sameness is what made the card feel cluttered: with no gap
          telling them apart, four bold labels and a paragraph all compete at one level.

          The card now runs three tiers: 8px inside a field (label to its value), 16px
          between spec fields, 24px from the prose to the specs. Proximity does the
          grouping, so nothing needed a divider or a heavier label.

          The space is PADDING ON A WRAPPER, and it took two wrong attempts to find that,
          both of which measured as no-ops:

          1. `pb` on the Typography itself. The clamp fixes its height to exactly N lines,
             so padding is absorbed INSIDE that box: it pulls the text up instead of pushing
             the next block down. 8px of padding moved the visual gap 16px -> 18px.
          2. `mb` on the Typography. MUI `Stack` with `spacing` emits
             `& > :not(style):not(style) { margin: 0 }` before applying its own margin-top,
             and that outspecifies the child's own sx class — so ANY margin set on a direct
             Stack child is silently zeroed. Computed margin-bottom came back 0px.

          That second rule is also why the `mt: -2` previously sitting here was dead code:
          computed margin-top was 16px, never -8px, so the "pull the description closer"
          intent had never once applied.

          A wrapper Box is the lever that works: the Box is the Stack child (its margins get
          zeroed, which is fine), and padding is not touched by that reset, so it adds real
          space below the fixed-height clamped Typography inside it.

          Safe for the grid alignment the clamp exists to protect: every non-hardware card
          gains the same 8px, so the badges still line up row to row.
        */}
        {!isHardware && (
          <Box sx={{ pb: 2 }}>
            <Typography
              variant="body3"
              color="text.secondary"
              sx={clampLinesSx(BRIEF_DESC_LINES)}
            >
              {plainDescription}
            </Typography>
          </Box>
        )}
        {/* Capabilities — a labelled field, rendered PLAIN so it matches the quiet
            treatment Sizes, Range and Best for already use on this card. Filled pills made
            Capabilities the loudest element on a card where capability is not the primary
            buying axis (form factor and Best-for are), and across the grid the badges are
            near-identical, so the colour was encoding sameness rather than difference. The
            colour still does real work on the product page, which keeps `filled`. */}
        {!isHardware && capabilities.length > 0 && (
          <Stack spacing={2}>
            <Typography variant="body3" sx={{ fontWeight: 600 }} noWrap>
              Capabilities
            </Typography>
            <TagCapabilityBadges capability={capabilities} size="md" variant="plain" />
          </Stack>
        )}
        {/* Sizes + Range as labelled fields (VRG-156) — bold label over the value,
            two columns so the card stays compact. */}
        {!isHardware && (sizeRange || rangeValue) && (
          <Stack direction="row" spacing={6}>
            {sizeRange && (
              <Stack spacing={2}>
                <Typography variant="body3" sx={{ fontWeight: 600 }} noWrap>Sizes</Typography>
                <Typography variant="body3" color="text.secondary">{sizeRange}</Typography>
              </Stack>
            )}
            {rangeValue && (
              <Stack spacing={2}>
                <Typography variant="body3" sx={{ fontWeight: 600 }} noWrap>Range</Typography>
                <Typography variant="body3" color="text.secondary">{rangeValue}</Typography>
              </Stack>
            )}
          </Stack>
        )}
        {/* Label->content gap standardised to 8px (was 2px) to match the Capabilities
            field above it. At 2px the label sat almost on top of its own content, which
            read as one clumped block rather than a labelled field. */}
        <Stack spacing={2}>
          <Typography variant="body3" sx={{ fontWeight: 600 }} noWrap>
            {isHardware ? 'Description' : 'Best for'}
          </Typography>
          {isHardware ? (
            <Typography
              variant="body3"
              color="text.secondary"
              sx={{ mt: 2, ...clampLinesSx(HARDWARE_DESC_LINES) }}
            >
              {/* `plainDescription`, not `product.description`: descriptions arrive as
                  HTML (that is why the strip exists at all), and the raw value rendered
                  its markup as literal visible text -- "<p>The Vera Desk Reader..." --
                  spending clamped lines on tags. */}
              {plainDescription || 'No description available'}
            </Typography>
          ) : (
          <Grid
            container
            ref={zoneRef}
            sx={{ height: CHIP_ZONE_HEIGHT, overflow: 'hidden', alignContent: 'flex-start' }}
          >
            {bestFor.length > 0 ? (
              <>
                {shown.map((category, index) => (
                  <Grid item key={index} sx={{ mr: 2, mt: 2 }}>
                    <CategoryCell category={category} filter />
                  </Grid>
                ))}
                {overflow > 0 && (
                  <Grid item sx={{ mr: 2, mt: 2 }}>
                    {/* Overflow peek — shared component so the card matches the product
                        page's "Best for +N more" popover exactly (VRG-156). */}
                    <MoreCategoriesTooltip categories={bestFor.slice(visibleCount)} filter>
                      <Stack justifyContent="center" alignItems="start" sx={{ minHeight: '100%' }}>
                        <Typography
                          variant="body3"
                          color="text.secondary"
                          noWrap
                          onClick={(e) => e.stopPropagation()}
                          sx={{ px: 1, cursor: 'default', '&:hover': { color: 'text.primary' } }}
                        >
                          + {overflow} more
                        </Typography>
                      </Stack>
                    </MoreCategoriesTooltip>
                  </Grid>
                )}
              </>
            ) : (
              <Grid item sx={{ mr: 2, mt: 2 }}>
                <Typography variant="body3" color="text.secondary">
                  No categories specified
                </Typography>
              </Grid>
            )}
          </Grid>
          )}
        </Stack>
      </Stack>
    </Card>
  )
}
